import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { useAddressesQuery } from '../../hooks/useAddress';
import { usePaymentMethodsQuery } from '../../hooks/usePaymentMethods';
import { useOrder } from '../../hooks/useOrder';
import { useOrderSummary } from '../../hooks/useOrderSummary';

export const useOrderDetails = () => {
  const { orderNumber = '' } = useParams<{ orderNumber?: string }>();

  const { addresses, loading: loadingAddresses, error: addressesError } = useAddressesQuery();
  const {
    data: paymentMethods,
    loading: loadingPaymentMethods,
    error: paymentMethodsError
  } = usePaymentMethodsQuery();
  const { order, error: orderError } = useOrder(orderNumber);
  const { orderSummary, error: orderSummaryError, orderItems } = useOrderSummary(orderNumber);

  if (addressesError) throwQueryError(addressesError);
  if (paymentMethodsError) throwQueryError(paymentMethodsError);
  if (orderError) throwQueryError(orderError);
  if (orderSummaryError) throwQueryError(orderSummaryError);

  const loading =
    loadingAddresses ||
    loadingPaymentMethods ||
    !addresses ||
    !paymentMethods ||
    !order ||
    !orderSummary ||
    !orderItems;

  const filteredOrderItems = useMemo(
    () =>
      orderItems?.filter(item =>
        order?.relationships.orderItems.data.map(({ id }) => id).includes(item.id)
      ) || [],
    [orderItems, order]
  );

  const hasBillFailure = useMemo(
    () =>
      filteredOrderItems.some(
        ({ attributes: { status, isRefundFailure } }) =>
          ['payment_decline_on_create', 'bill_failure'].includes(status) || isRefundFailure
      ),
    [filteredOrderItems]
  );

  return {
    orderNumber,
    order,
    orderSummary,
    orderItems,
    hasBillFailure,
    loading
  };
};
